/**
 * Game Status UI Component
 */

import type { GameState } from '../game/types.ts';

export interface GameStatusCallbacks {
  onResetGame: () => void;
  onNewGame: () => void;
}

/**
 * Create game status HTML element
 */
export function createGameStatus(
  gameState: GameState,
  callbacks: GameStatusCallbacks
): HTMLElement {
  const status = document.createElement('div');
  status.className = 'game-status';
  status.innerHTML = `
    <div class="status-header">
      <h2 id="game-title">GP2P Tic-Tac-Toe</h2>
      <div id="player-info" class="player-info">
        You are: <span id="player-symbol" class="player-symbol">${gameState.localPlayer}</span>
      </div>
    </div>
    
    <div id="status-message" class="status-message" role="status" aria-live="polite">
      ${getStatusMessage(gameState)}
    </div>
    
    <div class="game-controls">
      <button id="reset-game-btn" class="secondary-btn" ${gameState.status === 'waiting' ? 'disabled' : ''}>
        Reset Game
      </button>
      <button id="new-game-btn" class="primary-btn">
        New Game
      </button>
    </div>
    
    <div id="game-info" class="game-info">
      <div class="info-item">
        <span class="label">Status:</span>
        <span id="connection-status" class="value">Connected</span>
      </div>
      <div class="info-item">
        <span class="label">Turn:</span>
        <span id="turn-indicator" class="value">${getTurnText(gameState)}</span>
      </div>
    </div>
  `;

  setupGameStatusEvents(status, callbacks);
  return status;
}

/**
 * Setup event listeners for game status
 */
function setupGameStatusEvents(
  status: HTMLElement,
  callbacks: GameStatusCallbacks
): void {
  const resetBtn = status.querySelector('#reset-game-btn') as HTMLButtonElement;
  const newGameBtn = status.querySelector('#new-game-btn') as HTMLButtonElement;

  resetBtn.addEventListener('click', () => {
    callbacks.onResetGame();
  });

  newGameBtn.addEventListener('click', () => {
    callbacks.onNewGame();
  });
}

/**
 * Update game status display
 */
export function updateGameStatus(
  statusElement: HTMLElement,
  gameState: GameState
): void {
  const statusMessage = statusElement.querySelector(
    '#status-message'
  ) as HTMLElement;
  const playerSymbol = statusElement.querySelector(
    '#player-symbol'
  ) as HTMLElement;
  const turnIndicator = statusElement.querySelector(
    '#turn-indicator'
  ) as HTMLElement;
  const resetBtn = statusElement.querySelector(
    '#reset-game-btn'
  ) as HTMLButtonElement;

  // Update status message
  statusMessage.textContent = getStatusMessage(gameState);
  statusMessage.className = `status-message ${getStatusClass(gameState)}`;

  // Update player symbol
  playerSymbol.textContent = gameState.localPlayer;

  // Update turn indicator
  turnIndicator.textContent = getTurnText(gameState);

  // Update reset button state
  resetBtn.disabled = gameState.status === 'waiting';
}

/**
 * Get status message based on game state
 */
function getStatusMessage(gameState: GameState): string {
  switch (gameState.status) {
    case 'waiting':
      return 'Waiting for opponent to connect...';
    case 'playing':
      if (gameState.isMyTurn) {
        return 'Your turn - make a move!';
      } else {
        return "Waiting for opponent's move...";
      }
    case 'won':
      if (gameState.winner === gameState.localPlayer) {
        return '🎉 You won! Congratulations!';
      } else {
        return '😔 You lost. Better luck next time!';
      }
    case 'draw':
      return "🤝 It's a draw! Good game!";
    default:
      return 'Ready to play';
  }
}

/**
 * Get CSS class for status styling
 */
function getStatusClass(gameState: GameState): string {
  switch (gameState.status) {
    case 'waiting':
      return 'status-waiting';
    case 'playing':
      return gameState.isMyTurn ? 'status-my-turn' : 'status-opponent-turn';
    case 'won':
      return gameState.winner === gameState.localPlayer
        ? 'status-win'
        : 'status-lose';
    case 'draw':
      return 'status-draw';
    default:
      return '';
  }
}

/**
 * Get turn indicator text
 */
function getTurnText(gameState: GameState): string {
  if (gameState.status !== 'playing') {
    return '-';
  }

  if (gameState.isMyTurn) {
    return `Your turn (${gameState.localPlayer})`;
  } else {
    const opponent = gameState.localPlayer === 'X' ? 'O' : 'X';
    return `Opponent's turn (${opponent})`;
  }
}

/**
 * Update connection status in the info panel
 */
export function updateConnectionStatus(
  statusElement: HTMLElement,
  connected: boolean
): void {
  const connectionStatus = statusElement.querySelector(
    '#connection-status'
  ) as HTMLElement;
  connectionStatus.textContent = connected ? 'Connected' : 'Disconnected';
  connectionStatus.className = connected
    ? 'value status-connected'
    : 'value status-disconnected';
}
